import React from 'react';
import {
  Checkbox,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import {
  RECON_PRODUCTS,
  ReconProductValue,
  useMultiProductRecon,
  useReconGroup,
} from '../MultiProductReconContext';

interface ProductSelectorProps {
  reconId: string;
}

const ProductSelector = ({ reconId }: ProductSelectorProps) => {
  const { state } = useMultiProductRecon();
  const { recon, updateRecon } = useReconGroup(reconId);

  // A product already covered by another recon in this event can't be
  // selected twice - map each taken product to the recon that owns it.
  const takenByOtherRecon = new Map<ReconProductValue, number>();
  state.recons.forEach((r, index) => {
    if (r.id === reconId) return;
    r.products.forEach((p) => takenByOtherRecon.set(p, index + 1));
  });

  const toggle = (product: ReconProductValue, nextChecked: boolean) => {
    if (nextChecked) {
      if (!recon.products.includes(product)) {
        updateRecon({ products: [...recon.products, product] });
      }
    } else {
      updateRecon({ products: recon.products.filter((p) => p !== product) });
    }
  };

  return (
    <div className="space-y-1">
      <Label>Products</Label>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {RECON_PRODUCTS.map((product) => {
          const isSelected = recon.products.includes(product.value);
          const takenBy = takenByOtherRecon.get(product.value);
          const checkboxId = `mp-product-${reconId}-${product.value}`;
          const option = (
            <div
              key={product.value}
              className="flex items-center space-x-2"
            >
              <Checkbox
                id={checkboxId}
                checked={isSelected}
                disabled={takenBy !== undefined}
                onCheckedChange={(value) =>
                  toggle(product.value, value === true)
                }
              />
              <Label
                htmlFor={checkboxId}
                className={`font-normal !leading-none ${
                  takenBy !== undefined
                    ? 'cursor-not-allowed opacity-50'
                    : ''
                }`}
              >
                {product.label}
              </Label>
            </div>
          );
          if (takenBy === undefined) return option;
          return (
            <Tooltip key={product.value}>
              <TooltipTrigger asChild>{option}</TooltipTrigger>
              <TooltipContent>
                Already selected in Recon {takenBy}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default ProductSelector;
